/**
 * Base Code API
 * 기본 코드 조회 API
 */

import { apiClient } from './client';
import type { BaseCode, BaseCodeListResponse, BaseCodeResponse } from '../types/baseCode.types';

export const baseCodeApi = {
  /**
   * 특정 기본 코드 조회 (국가 코드 + 전체 코드)
   * @param countryCode - 국가 코드 (예: 'SG')
   * @param totalCode - 전체 코드 (예: '001000')
   * @param languageCode - 언어 코드 (선택)
   * @returns 기본 코드 정보
   */
  getCode: (countryCode: string, totalCode: string, languageCode?: string) => {
    const params = languageCode ? { languageCode } : {};
    return apiClient.get<BaseCodeResponse>(`/basecode/${countryCode}/${totalCode}`, { params });
  },

  /**
   * TotalCode만으로 기본 코드 조회
   * @param countryCode - 국가 코드 (예: 'SG')
   * @param totalCode - 전체 코드 (예: '001000')
   * @param languageCode - 언어 코드 (선택)
   * @returns 기본 코드 정보
   */
  getCodeByTotalCode: (countryCode: string, totalCode: string, languageCode?: string) => {
    const params: Record<string, string> = { countryCode };
    if (languageCode) {
      params.languageCode = languageCode;
    }
    return apiClient.get<BaseCodeResponse>(`/basecode/total/${totalCode}`, { params });
  },

  /**
   * 메인 코드로 그룹 조회 (하위 코드 목록)
   * @param countryCode - 국가 코드 (예: 'SG')
   * @param mainCode - 메인 코드 (예: '001')
   * @param languageCode - 언어 코드 (선택)
   * @returns 기본 코드 목록
   */
  getCodesByMainCode: (countryCode: string, mainCode: string, languageCode?: string) => {
    const params = languageCode ? { languageCode } : {};
    return apiClient.get<BaseCodeListResponse>(`/basecode/main/${countryCode}/${mainCode}`, { params });
  },

  /**
   * MainCode만으로 기본 코드 목록 조회 (국가 코드 불필요, 모든 국가의 코드 반환)
   * @param mainCode - 메인 코드 (예: '001')
   * @param languageCode - 언어 코드 (선택)
   * @returns 기본 코드 목록
   */
  getCodesByMainCodeOnly: (mainCode: string, languageCode?: string) => {
    const params = languageCode ? { languageCode } : {};
    return apiClient.get<BaseCodeListResponse>(`/basecode/main/${mainCode}`, { params });
  },

  /**
   * 지원 언어 목록 조회 (L_LANGUAGES 테이블에서 조회)
   * @returns 지원 언어 목록
   */
  getLanguages: () => {
    return apiClient.get<{
      languages: Array<{
        code: string;
        name: string;
        nativeName: string;
        flag: string;
        countryCode: string;
        isDefault: boolean;
        displayOrder: number;
      }>;
      defaultLanguage: string;
      count: number;
    }>('/basecode/languages');
  },

  /**
   * 업무용: TotalCode만으로 기본 코드 조회 (세션의 jurisdiction에서 countryCode 자동 추출)
   * @param totalCode - 전체 코드 (예: '001000')
   * @param languageCode - 언어 코드 (선택)
   * @returns 기본 코드 정보
   */
  getCodeByTotalCodeAuto: (totalCode: string, languageCode?: string) => {
    const params = languageCode ? { languageCode } : {};
    return apiClient.get<BaseCodeResponse>(`/basecode/auto/total/${totalCode}`, { params });
  },

  /**
   * 업무용: MainCode로 기본 코드 목록 조회 (세션의 jurisdiction에서 countryCode 자동 추출)
   * @param mainCode - 메인 코드 (예: 'A23')
   * @param languageCode - 언어 코드 (선택)
   * @returns 기본 코드 목록
   */
  getCodesByMainCodeAuto: (mainCode: string, languageCode?: string) => {
    const params = languageCode ? { languageCode } : {};
    return apiClient.get<BaseCodeListResponse>(`/basecode/auto/main/${mainCode}`, { params });
  },
};

